import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import {
  fetchColaboradores,
  fetchDuplicadosColaboradores,
  mesclarDuplicadoPar,
  mesclarDuplicadosAuto,
  type DuplicadoColabInfo,
} from './api'
import { ImportPipelineNav } from './ImportPipelineNav'

function cpfLabel(c: DuplicadoColabInfo) {
  if (!c.cpf) return '—'
  return c.cpf_sintetico ? `${c.cpf} (sintético)` : c.cpf
}

function CardColab({
  c,
  destaque,
}: {
  c: DuplicadoColabInfo
  destaque?: boolean
}) {
  return (
    <div className={destaque ? 'hi-dup-card hi-dup-card-keep' : 'hi-dup-card'}>
      <strong>{c.nome || '—'}</strong>
      <Muted>
        CPF {cpfLabel(c)}
        {c.matricula ? ` · mat. ${c.matricula}` : ''}
        {c.id_bitrix ? ` · Bitrix ${c.id_bitrix}` : ''}
      </Muted>
      <Muted>
        {c.email || 'sem e-mail'}
        {c.departamento ? ` · ${c.departamento}` : ''}
        {c.tem_foto ? ' · foto' : ''}
      </Muted>
    </div>
  )
}

export function MesclarDuplicadosPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('colaboradores')
  const qc = useQueryClient()

  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState(false)
  const [buscaA, setBuscaA] = useState('')
  const [buscaB, setBuscaB] = useState('')
  const [manterId, setManterId] = useState('')
  const [removerId, setRemoverId] = useState('')
  const [forcar, setForcar] = useState(false)

  const lista = useQuery({
    queryKey: ['rh', 'duplicados'],
    queryFn: fetchDuplicadosColaboradores,
    enabled: editable,
  })

  const optsA = useQuery({
    queryKey: ['rh', 'colaboradores', 'dup-a', buscaA],
    queryFn: () =>
      fetchColaboradores({ busca: buscaA || undefined, status: 'ativo', por_pagina: 20 }),
    enabled: editable && buscaA.trim().length >= 2,
  })

  const optsB = useQuery({
    queryKey: ['rh', 'colaboradores', 'dup-b', buscaB],
    queryFn: () =>
      fetchColaboradores({ busca: buscaB || undefined, status: 'ativo', por_pagina: 20 }),
    enabled: editable && buscaB.trim().length >= 2,
  })

  const autoMut = useMutation({
    mutationFn: (dryRun: boolean) => mesclarDuplicadosAuto({ dryRun }),
    onSuccess: async (res, dryRun) => {
      setErro(false)
      setMsg(
        dryRun
          ? `Simulação: ${res.pares ?? 0} par(es) seriam mesclados.`
          : `Mesclados automaticamente: ${res.mesclados ?? 0} de ${res.pares ?? 0} par(es).`,
      )
      await qc.invalidateQueries({ queryKey: ['rh', 'duplicados'] })
      await qc.invalidateQueries({ queryKey: ['rh', 'colaboradores'] })
    },
    onError: (err) => {
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha na mescla automática.')
    },
  })

  const parMut = useMutation({
    mutationFn: (opts: { manterId: string; removerId: string; forcar?: boolean }) =>
      mesclarDuplicadoPar({
        manterId: opts.manterId,
        removerId: opts.removerId,
        forcar: opts.forcar,
      }),
    onSuccess: async (res) => {
      if (res.sucesso === false) {
        setErro(true)
        setMsg(res.mensagem || 'Não foi possível vincular.')
        return
      }
      setErro(false)
      setMsg(res.mensagem || 'Duplicata vinculada.')
      setManterId('')
      setRemoverId('')
      setBuscaA('')
      setBuscaB('')
      await qc.invalidateQueries({ queryKey: ['rh', 'duplicados'] })
      await qc.invalidateQueries({ queryKey: ['rh', 'colaboradores'] })
    },
    onError: (err) => {
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha ao vincular o par.')
    },
  })

  const pares = lista.data?.dados || []
  const pending = autoMut.isPending || parMut.isPending

  const selecionados = useMemo(() => {
    const a = optsA.data?.dados?.find((c) => c.id === manterId)
    const b = optsB.data?.dados?.find((c) => c.id === removerId)
    return { a, b }
  }, [optsA.data, optsB.data, manterId, removerId])

  if (!editable) {
    return (
      <section className="hi-page">
        <PageHeader title="Mesclar duplicados" subtitle="Acesso restrito a RH/Admin." />
        <Alert tone="danger">Sem permissão.</Alert>
      </section>
    )
  }

  return (
    <section className="hi-page hi-import-colab-page">
      <PageHeader
        title="Mesclar duplicados"
        subtitle="Vincula dois cadastros da mesma pessoa (ex.: folha + Bitrix com CPF sintético). Mantém o mais completo e desliga o duplicado."
        actions={
          <Link className="btn btn-secondary" to="/app/rh">
            Voltar à lista
          </Link>
        }
      />

      <ImportPipelineNav active="duplicados" />

      {msg ? <Alert tone={erro ? 'danger' : 'info'}>{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Pares sugeridos" value={String(lista.data?.total ?? pares.length)} />
      </KpiGrid>

      <Section title="Sugestões automáticas">
        <div className="hi-filters hi-import-form">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={pending || lista.isLoading}
            onClick={() => autoMut.mutate(true)}
          >
            Simular todos
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={pending || pares.length === 0}
            onClick={() => {
              if (
                confirm(
                  `Mesclar ${pares.length} par(es) sugeridos? O cadastro mais completo é mantido; o outro é desligado.`,
                )
              ) {
                autoMut.mutate(false)
              }
            }}
          >
            Mesclar todos sugeridos
          </button>
        </div>
        <Muted>
          Só une pares seguros: mesmo nome compatível, um com Bitrix e outro sem, ou CPF sintético
          (prefixo 8) × CPF real. Nunca une dois CPFs reais diferentes.
        </Muted>

        {lista.isLoading ? <Muted>Carregando…</Muted> : null}
        {lista.isError ? (
          <Alert tone="danger">
            {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
          </Alert>
        ) : null}
        {!lista.isLoading && pares.length === 0 ? (
          <Empty>Nenhuma duplicata sugerida no momento.</Empty>
        ) : null}

        {pares.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Manter (sugerido)</th>
                <th>Remover (sugerido)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pares.map((p) => {
                const manter =
                  p.a.id === p.sugerido_manter_id ? p.a : p.b
                const remover =
                  p.a.id === p.sugerido_remover_id ? p.a : p.b
                return (
                  <tr key={`${p.a.id}-${p.b.id}`}>
                    <td>
                      <CardColab c={manter} destaque />
                    </td>
                    <td>
                      <CardColab c={remover} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={pending}
                        onClick={() => {
                          if (
                            confirm(
                              `Manter “${manter.nome}” e desligar “${remover.nome}”?`,
                            )
                          ) {
                            parMut.mutate({
                              manterId: manter.id,
                              removerId: remover.id,
                              forcar: false,
                            })
                          }
                        }}
                      >
                        Vincular
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={pending}
                        style={{ marginTop: 6 }}
                        onClick={() => {
                          if (
                            confirm(
                              `Inverter: manter “${remover.nome}” e desligar “${manter.nome}”?`,
                            )
                          ) {
                            parMut.mutate({
                              manterId: remover.id,
                              removerId: manter.id,
                              forcar: true,
                            })
                          }
                        }}
                      >
                        Inverter
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
        ) : null}
      </Section>

      <Section title="Vincular manualmente">
        <Muted>
          Busque e selecione os dois cadastros. Use “forçar” só se tiver certeza e a sugestão
          automática não listou o par.
        </Muted>
        <form
          className="hi-filters hi-import-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!manterId || !removerId) {
              setErro(true)
              setMsg('Selecione os dois colaboradores.')
              return
            }
            if (
              confirm(
                'Vincular estes dois cadastros? O “remover” será desligado e os dados úteis passam para o “manter”.',
              )
            ) {
              parMut.mutate({ manterId, removerId, forcar })
            }
          }}
        >
          <label>
            Manter
            <input
              type="search"
              value={buscaA}
              onChange={(e) => setBuscaA(e.target.value)}
              placeholder="Buscar nome/CPF…"
              disabled={pending}
            />
            <select
              value={manterId}
              onChange={(e) => setManterId(e.target.value)}
              aria-label="Colaborador a manter"
              disabled={pending}
            >
              <option value="">Selecione…</option>
              {(optsA.data?.dados || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} — {c.cpf || 'sem CPF'}
                </option>
              ))}
            </select>
          </label>
          <label>
            Remover (duplicado)
            <input
              type="search"
              value={buscaB}
              onChange={(e) => setBuscaB(e.target.value)}
              placeholder="Buscar nome/CPF…"
              disabled={pending}
            />
            <select
              value={removerId}
              onChange={(e) => setRemoverId(e.target.value)}
              aria-label="Colaborador a remover"
              disabled={pending}
            >
              <option value="">Selecione…</option>
              {(optsB.data?.dados || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} — {c.cpf || 'sem CPF'}
                </option>
              ))}
            </select>
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={forcar}
              disabled={pending}
              onChange={(e) => setForcar(e.target.checked)}
            />
            Forçar (par fora da regra automática)
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={pending || !manterId || !removerId}
          >
            {pending ? 'Vinculando…' : 'Vincular estes dois'}
          </button>
        </form>
        {selecionados.a || selecionados.b ? (
          <div className="hi-dup-manual-preview">
            {selecionados.a ? (
              <div>
                <StatusBadge tone="ativo">Manter</StatusBadge>
                <CardColab c={{
                  id: selecionados.a.id,
                  nome: selecionados.a.nome,
                  cpf: selecionados.a.cpf,
                  email:
                    selecionados.a.email_corporativo
                    || selecionados.a.email_profissional
                    || '',
                }} destaque />
              </div>
            ) : null}
            {selecionados.b ? (
              <div>
                <StatusBadge tone="inativo">Remover</StatusBadge>
                <CardColab c={{
                  id: selecionados.b.id,
                  nome: selecionados.b.nome,
                  cpf: selecionados.b.cpf,
                  email:
                    selecionados.b.email_corporativo
                    || selecionados.b.email_profissional
                    || '',
                }} />
              </div>
            ) : null}
          </div>
        ) : null}
      </Section>
    </section>
  )
}

export default MesclarDuplicadosPage
